<template>
  <AppHeader />
  <div class="min-h-screen bg-blue-50 pb-20">
    <!-- Header section with UP colors -->
    <div class="bg-gradient-to-r from-blue-900 to-blue-700 py-8 shadow-md">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex flex-col sm:flex-row items-center">
          <div class="relative mb-4 sm:mb-0 sm:mr-8">
            <img 
              :src="profile?.profilePicture || require('@/assets/defaultProfile.png')" 
              alt="Profile Picture"
              class="w-24 h-24 rounded-full object-cover border-2 border-yellow-500 shadow-lg"
            />
            <router-link 
              to="/update-profile-picture"
              class="absolute bottom-0 right-0 bg-white p-1.5 rounded-full shadow-md hover:bg-gray-100 transition-colors"
            >
              <span class="material-symbols-outlined text-sm text-blue-800">photo_camera</span>
            </router-link>
          </div>
          <div class="text-center sm:text-left">
            <h1 class="text-2xl font-bold text-white mb-1">{{ profile?.name || 'User' }} {{ profile?.surname || '' }}</h1>
            <p class="text-blue-100 mb-1">{{ profile?.email || 'user@email.com' }}</p>
            <p class="text-xs text-blue-200">Member since {{ profile?.registrationDateShort || 'N/A' }}</p>
            <div class="mt-3 flex justify-center sm:justify-start">
              <span
                class="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border"
                :class="isAdmin ? 'bg-yellow-100 text-yellow-800 border-yellow-200' : 'bg-blue-100 text-blue-800 border-blue-200'"
              >
                <span class="material-symbols-outlined text-sm mr-1">{{ isAdmin ? 'admin_panel_settings' : 'school' }}</span>
                {{ isAdmin ? 'Administrator' : 'Student' }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading state -->
    <div v-if="loading" class="flex flex-col items-center justify-center py-12">
      <div class="w-10 h-10 border-4 border-blue-200 border-t-blue-800 rounded-full animate-spin mb-4"></div>
      <p class="text-blue-800">Loading your profile...</p>
    </div>

    <!-- Main content -->
    <div v-else class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-24">
      <router-link
        v-if="isAdmin"
        to="/admin"
        class="lg:hidden block bg-white rounded-lg shadow mb-6"
      >
        <div class="px-6 py-4 flex items-center justify-between">
          <div class="flex items-center min-w-0">
            <div class="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center mr-3 shrink-0">
              <span class="material-symbols-outlined text-yellow-700">admin_panel_settings</span>
            </div>
            <div class="text-left min-w-0">
              <h2 class="text-lg font-semibold text-gray-800 truncate">Admin Panel</h2>
              <p class="text-sm text-gray-500 truncate">Manage users &amp; view stats</p>
            </div>
          </div>
          <span class="material-symbols-outlined text-gray-400 shrink-0">chevron_right</span>
        </div>
      </router-link>

      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        <!-- Left column for personal and university info -->
        <div class="lg:col-span-2 space-y-6">
          <!-- Personal Information Card -->
          <div class="bg-white rounded-lg shadow transform transition-transform duration-300 hover:-translate-y-1 hover:shadow-md">
            <div class="flex justify-between items-center px-6 py-4 border-b border-gray-100">
              <div class="flex items-center">
                <span class="material-symbols-outlined text-blue-800 mr-3">person</span>
                <h2 class="text-lg font-semibold text-gray-800">Personal Information</h2>
              </div>
              <router-link to="/update-profile" class="text-blue-800 hover:text-yellow-500 transition-colors flex items-center">
                <span class="material-symbols-outlined">edit</span>
                <span class="hidden sm:inline ml-1">Edit</span>
              </router-link>
            </div>
            
            <div class="divide-y divide-gray-100">
              <div class="flex items-center px-6 py-4">
                <div class="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-4">
                  <span class="material-symbols-outlined text-blue-800">badge</span>
                </div>
                <div>
                  <p class="text-xs text-gray-500 text-left">Full Name</p>
                  <p class="font-medium text-gray-900 text-left">{{ profile?.name || 'N/A' }} {{ profile?.surname || '' }}</p>
                </div>
              </div>
              
              <div class="flex items-center px-6 py-4">
                <div class="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-4">
                  <span class="material-symbols-outlined text-blue-800">mail</span>
                </div>
                <div class="flex-1">
                  <p class="text-xs text-gray-500 text-left">Email Address</p>
                  <div class="flex justify-between items-center">
                    <p class="font-medium text-gray-900 text-left">{{ profile?.email || 'N/A' }}</p>
                    <div v-if="user" class="ml-2 flex items-center">
                      <span v-if="user.emailVerified" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <span class="material-symbols-outlined text-sm mr-1">check_circle</span>
                        Verified
                      </span>
                      <span v-else class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        <span class="material-symbols-outlined text-sm mr-1">error</span>
                        Not Verified
                      </span>
                    </div>
                  </div>
                  <div v-if="user && !user.emailVerified" class="mt-2 text-left" style="display: none;">
                    <p class="text-xs text-yellow-600 mb-1">Please verify your email address to access all features</p>
                    <button 
                      @click="sendVerificationEmail" 
                      class="text-xs text-blue-700 hover:text-blue-900 flex items-center"
                      :disabled="verificationEmailSent"
                    >
                      <span class="material-symbols-outlined text-sm mr-1">send</span>
                      {{ verificationEmailSent ? 'Verification email sent!' : 'Send verification email' }}
                    </button>
                  </div>
                </div>
              </div>
              
              <div class="flex items-center px-6 py-4">
                <div class="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-4">
                  <span class="material-symbols-outlined text-blue-800">calendar_month</span>
                </div>
                <div>
                  <p class="text-xs text-gray-500 text-left">Member Since</p>
                  <div>
                    <span class="font-medium text-gray-900 text-left">{{ profile?.registrationDateShort || 'N/A' }}</span>
                    <span class="text-sm text-gray-500 ml-2 text-left">({{ profile?.registrationDay || 'N/A' }})</span>
                  </div>
                </div>
              </div>

              <div class="flex items-center px-6 py-4">
                <div class="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-4">
                  <span class="material-symbols-outlined text-blue-800">verified_user</span>
                </div>
                <div>
                  <p class="text-xs text-gray-500 text-left">Account Type</p>
                  <p class="font-medium text-gray-900 text-left">{{ profile?.role === 'admin' ? 'Administrator' : 'Student' }}</p>
                </div>
              </div>
            </div>
          </div>

          <!-- University Information Card -->
          <div class="bg-white rounded-lg shadow transform transition-transform duration-300 hover:-translate-y-1 hover:shadow-md">
            <div class="flex justify-between items-center px-6 py-4 border-b border-gray-100">
              <div class="flex items-center">
                <span class="material-symbols-outlined text-blue-800 mr-3">school</span>
                <h2 class="text-lg font-semibold text-gray-800">University Information</h2>
              </div>
              <router-link to="/update-university" class="text-blue-800 hover:text-yellow-500 transition-colors flex items-center">
                <span class="material-symbols-outlined">edit</span>
                <span class="hidden sm:inline ml-1">Edit</span>
              </router-link>
            </div>
            
            <div class="divide-y divide-gray-100">
              <div class="flex items-center px-6 py-4">
                <div class="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-4">
                  <span class="material-symbols-outlined text-blue-800">account_balance</span>
                </div>
                <div>
                  <p class="text-xs text-gray-500 text-left">University</p>
                  <p class="font-medium text-gray-900 text-left">{{ profile?.university?.name || 'Not specified' }}</p>
                </div>
              </div>
              
              <div class="flex items-center px-6 py-4">
                <div class="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-4">
                  <span class="material-symbols-outlined text-blue-800">business</span>
                </div>
                <div>
                  <p class="text-xs text-gray-500 text-left">Faculty</p>
                  <p class="font-medium text-gray-900 text-left">{{ profile?.university?.faculty || 'Not specified' }}</p>
                </div>
              </div>

              <!-- Course Name (replaces Department) -->
              <div class="flex items-center px-6 py-4">
                <div class="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-4">
                  <span class="material-symbols-outlined text-blue-800">menu_book</span>
                </div>
                <div>
                  <p class="text-xs text-gray-500 text-left">Course Name</p>
                  <p class="font-medium text-gray-900 text-left">{{ profile?.university?.courseName || 'Not specified' }}</p>
                </div>
              </div>

              <!-- Study Year (optional) -->
              <div v-if="profile?.university?.studyYear" class="flex items-center px-6 py-4">
                <div class="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-4">
                  <span class="material-symbols-outlined text-blue-800">calendar_month</span>
                </div>
                <div>
                  <p class="text-xs text-gray-500 text-left">Study Year</p>
                  <p class="font-medium text-gray-900 text-left">{{ profile.university.studyYear }}</p>
                </div>
              </div>
              
              <div class="flex items-center px-6 py-4">
                <div class="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-4">
                  <span class="material-symbols-outlined text-blue-800">stairs</span>
                </div>
                <div>
                  <p class="text-xs text-gray-500 text-left">Level of Study</p>
                  <p class="font-medium text-gray-900 text-left">{{ profile?.university?.levelOfStudy || 'Not specified' }}</p>
                </div>
              </div>
              
              <div class="flex items-start px-6 py-4">
                <div class="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-4 mt-1">
                  <span class="material-symbols-outlined text-blue-800">apartment</span>
                </div>
                <div class="flex-1">
                  <p class="text-xs text-gray-500 text-left">Campus</p>
                  <div v-if="profile?.university?.campuses && profile.university.campuses.length > 0" class="flex flex-wrap gap-2 mt-1 text-left">
                    <span 
                      v-for="(campus, index) in profile.university.campuses" 
                      :key="index" 
                      class="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full"
                    >
                      {{ campus }}
                    </span>
                  </div>
                  <div v-else-if="profile?.university?.campus" class="flex flex-wrap gap-2 mt-1">
                    <span class="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full text-left">
                      {{ profile.university.campus }}
                    </span>
                  </div>
                  <p v-else class="font-medium text-gray-900 text-left">Not specified</p>
                </div>
              </div>

              <!-- New section to display modules if available -->
              <div v-if="profile?.university?.modules && profile.university.modules.length > 0" class="px-6 py-4">
                <div class="flex items-center justify-between mb-3">
                  <div class="flex items-center">
                    <div class="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-4">
                      <span class="material-symbols-outlined text-blue-800">list_alt</span>
                    </div>
                    <div>
                      <p class="text-xs text-gray-500 text-left">Modules</p>
                      <p class="font-medium text-gray-900 text-left">{{ profile.university.modules.length }} modules</p>
                    </div>
                  </div>
                  <button
                    @click="showModulesModal = !showModulesModal"
                    class="text-blue-600 hover:text-blue-800 text-sm flex items-center"
                  >
                    <span>{{ showModulesModal ? 'Hide' : 'View all' }}</span>
                    <span class="material-symbols-outlined text-sm ml-1">
                      {{ showModulesModal ? 'expand_less' : 'expand_more' }}
                    </span>
                  </button>
                </div>
                
                <!-- Modules preview (show first few) -->
                <div v-if="!showModulesModal" class="flex flex-wrap gap-2 mt-1 text-left">
                  <span 
                    v-for="(module, idx) in profile.university.modules.slice(0, 5)" 
                    :key="idx"
                    class="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full"
                  >
                    {{ module }}
                  </span>
                  <span v-if="profile.university.modules.length > 5" class="text-gray-500 text-xs flex items-center">
                    and {{ profile.university.modules.length - 5 }} more...
                  </span>
                </div>
                
                <!-- Full modules list (when expanded) -->
                <div v-if="showModulesModal" class="mt-3">
                  <div class="bg-blue-50 p-3 rounded-lg max-h-48 overflow-y-auto">
                    <div class="flex flex-wrap gap-2 text-left">
                      <span 
                        v-for="(module, idx) in profile.university.modules" 
                        :key="idx"
                        class="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full"
                      >
                        {{ module }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Privacy Settings Card (NEW) -->
          <div class="bg-white rounded-lg shadow transform transition-transform duration-300 hover:-translate-y-1 hover:shadow-md">
            <div class="flex justify-between items-center px-6 py-4 border-b border-gray-100">
              <div class="flex items-center">
                <span class="material-symbols-outlined text-blue-800 mr-3">visibility</span>
                <h2 class="text-lg font-semibold text-gray-800">Privacy Settings</h2>
              </div>
              <button
                @click="showPrivacySettings = !showPrivacySettings"
                class="text-blue-800 hover:text-yellow-500 transition-colors flex items-center"
              >
                <span class="material-symbols-outlined">
                  {{ showPrivacySettings ? 'expand_less' : 'expand_more' }}
                </span>
              </button>
            </div>
            
            <div v-if="showPrivacySettings" class="px-6 py-4">
              <p class="text-sm text-gray-600 mb-4">
                Control what information is visible to other students in the Social Hub. Your name and profile picture are always visible to connections.
              </p>
              
              <div class="space-y-4">
                <!-- Faculty Visibility -->
                <div class="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div class="flex items-center">
                    <span class="material-symbols-outlined text-blue-700 mr-3">science</span>
                    <div>
                      <p class="font-medium text-gray-900 text-left">Faculty</p>
                      <p class="text-xs text-gray-500 text-left">{{ profile?.university?.faculty || 'Not set' }}</p>
                    </div>
                  </div>
                  <label class="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      v-model="privacySettings.showFaculty"
                      @change="savePrivacySettings"
                      class="sr-only peer"
                    />
                    <div class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>

                <!-- Course Name Visibility -->
                <div class="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div class="flex items-center">
                    <span class="material-symbols-outlined text-blue-700 mr-3">menu_book</span>
                    <div>
                      <p class="font-medium text-gray-900 text-left">Course Name</p>
                      <p class="text-xs text-gray-500 text-left">{{ profile?.university?.courseName || 'Not set' }}</p>
                    </div>
                  </div>
                  <label class="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      v-model="privacySettings.showCourseName"
                      @change="savePrivacySettings"
                      class="sr-only peer"
                    />
                    <div class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>

                <!-- Study Year Visibility -->
                <div class="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div class="flex items-center">
                    <span class="material-symbols-outlined text-blue-700 mr-3">calendar_month</span>
                    <div>
                      <p class="font-medium text-gray-900 text-left">Study Year</p>
                      <p class="text-xs text-gray-500 text-left">{{ profile?.university?.studyYear || 'Not set' }}</p>
                    </div>
                  </div>
                  <label class="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      v-model="privacySettings.showStudyYear"
                      @change="savePrivacySettings"
                      class="sr-only peer"
                    />
                    <div class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>

                <!-- Level of Study Visibility -->
                <div class="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div class="flex items-center">
                    <span class="material-symbols-outlined text-blue-700 mr-3">stairs</span>
                    <div>
                      <p class="font-medium text-gray-900 text-left">Level of Study</p>
                      <p class="text-xs text-gray-500 text-left">{{ profile?.university?.levelOfStudy || 'Not set' }}</p>
                    </div>
                  </div>
                  <label class="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      v-model="privacySettings.showLevelOfStudy"
                      @change="savePrivacySettings"
                      class="sr-only peer"
                    />
                    <div class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>

                <!-- Campus Visibility -->
                <div class="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div class="flex items-center">
                    <span class="material-symbols-outlined text-blue-700 mr-3">location_on</span>
                    <div>
                      <p class="font-medium text-gray-900 text-left">Campus</p>
                      <p class="text-xs text-gray-500 text-left">
                        {{ profile?.university?.campuses?.join(', ') || profile?.university?.campus || 'Not set' }}
                      </p>
                    </div>
                  </div>
                  <label class="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      v-model="privacySettings.showCampus"
                      @change="savePrivacySettings"
                      class="sr-only peer"
                    />
                    <div class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              </div>

              <div class="mt-4 p-3 bg-blue-50 rounded-lg">
                <div class="flex items-start">
                  <span class="material-symbols-outlined text-blue-600 text-sm mr-2">info</span>
                  <p class="text-xs text-blue-800">
                    These settings only affect what other students see in the Social Hub. Your university name is always visible to help others find peers from the same institution.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Right column for actions and activity summary -->
        <div class="lg:col-span-1 space-y-6">
          <!-- Admin Panel Card (admins only) -->
          <router-link
            v-if="isAdmin"
            to="/admin"
            class="hidden lg:block bg-white rounded-lg shadow transform transition-transform duration-300 hover:-translate-y-1 hover:shadow-md"
          >
            <div class="px-6 py-4 flex items-center justify-between">
              <div class="flex items-center">
                <div class="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center mr-3">
                  <span class="material-symbols-outlined text-yellow-700">admin_panel_settings</span>
                </div>
                <div class="text-left">
                  <h2 class="text-lg font-semibold text-gray-800">Admin Panel</h2>
                  <p class="text-sm text-gray-500">Manage users &amp; view stats</p>
                </div>
              </div>
              <span class="material-symbols-outlined text-gray-400">chevron_right</span>
            </div>
          </router-link>

          <!-- Account Actions Card -->
          <div class="bg-white rounded-lg shadow transform transition-transform duration-300 hover:-translate-y-1 hover:shadow-md">
            <div class="px-6 py-4 border-b border-gray-100">
              <div class="flex items-center">
                <span class="material-symbols-outlined text-blue-800 mr-3">settings</span>
                <h2 class="text-lg font-semibold text-gray-800">Account Settings</h2>
              </div>
            </div>
            
            <div class="divide-y divide-gray-100">
              <router-link to="/change-password" 
                class="flex items-center justify-between px-6 py-4 hover:bg-blue-50 transition-colors"
              >
                <div class="flex items-center">
                  <div class="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                    <span class="material-symbols-outlined text-blue-800 text-sm">lock</span>
                  </div>
                  <span class="font-medium text-gray-900">Change Password</span>
                </div>
                <span class="material-symbols-outlined text-gray-400">chevron_right</span>
              </router-link>
              
              <button @click="logout" 
                class="w-full flex items-center justify-between px-6 py-4 hover:bg-blue-50 transition-colors text-left"
              >
                <div class="flex items-center">
                  <div class="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                    <span class="material-symbols-outlined text-blue-800 text-sm">logout</span>
                  </div>
                  <span class="font-medium text-gray-900">Log Out</span>
                </div>
                <span class="material-symbols-outlined text-gray-400">chevron_right</span>
              </button>
              
              <button @click="$router.push('/delete-account')" 
                class="w-full flex items-center justify-between px-6 py-4 hover:bg-red-50 transition-colors text-left"
              >
                <div class="flex items-center">
                  <div class="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center mr-3">
                    <span class="material-symbols-outlined text-red-600 text-sm">delete</span>
                  </div>
                  <span class="font-medium text-red-600">Delete Account</span>
                </div>
                <span class="material-symbols-outlined text-red-300">chevron_right</span>
              </button>
            </div>
          </div>

          <!-- Activity Summary Card -->
          <div class="bg-white rounded-lg shadow transform transition-transform duration-300 hover:-translate-y-1 hover:shadow-md">
            <div class="px-6 py-4 border-b border-gray-100">
              <div class="flex items-center">
                <span class="material-symbols-outlined text-blue-800 mr-3">insights</span>
                <h2 class="text-lg font-semibold text-gray-800">Activity Summary</h2>
              </div>
            </div>
            
            <div>
              <div class="text-center py-6">
                <div class="mb-4">
                  <span class="material-symbols-outlined text-4xl text-blue-800">calendar_month</span>
                </div>
                <p class="text-gray-500 mb-1">Member for</p>
                <p class="text-xl font-bold">{{ daysSinceRegistration }} {{ daysSinceRegistration === 1 ? 'day' : 'days' }}</p>
              </div>

              <div class="border-t border-gray-100 mx-6"></div>

              <div class="text-center py-6 px-6">
                <p class="text-sm text-gray-500 mb-4">Complete your profile to unlock all features</p>
                <div class="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div 
                    class="h-full bg-gradient-to-r from-blue-800 to-blue-600 rounded-full"
                    :style="`width: ${profileCompletionPercentage}%`"
                  ></div>
                </div>
                <p class="text-xs text-right mt-1 text-gray-500">{{ profileCompletionPercentage }}% complete</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <BottomNav />
</template>

<script>
import AppHeader from '@/components/common/AppHeader.vue';
import BottomNav from '@/components/common/BottomNav.vue';
import { mapState } from 'vuex';
import { logoutUser, sendVerificationEmail } from '@/composables/useAuth';

export default {
  components: {
    AppHeader,
    BottomNav,
  },
  data() {
    return {
      showDeleteInput: false,
      verificationEmailSent: false,
      showModulesModal: false,
      showPrivacySettings: false,
      privacySettings: {
        showFaculty: true,
        showCourseName: true,
        showStudyYear: true,
        showLevelOfStudy: true,
        showCampus: true
      }
    };
  },
  computed: {
    ...mapState(['user', 'profile', 'loading', 'globalSettings', 'curricula']),
    isAdmin() {
      return this.profile && this.profile.role === 'admin';
    },
    currentPlan() {
      if (!this.profile?.plan?.name || !this.globalSettings?.plans) return null;
      return this.globalSettings.plans[this.profile.plan.name.toLowerCase()];
    },
    daysSinceRegistration() {
      if (!this.profile?.registrationDate) return 0;
      
      // If registrationDate is a timestamp or string, convert it to a Date object
      let regDate;
      if (this.profile.registrationDate instanceof Date) {
        regDate = this.profile.registrationDate;
      } else if (typeof this.profile.registrationDate === 'object' && this.profile.registrationDate.seconds) {
        // Firebase Timestamp
        regDate = new Date(this.profile.registrationDate.seconds * 1000);
      } else {
        regDate = new Date(this.profile.registrationDateString || this.profile.registrationDateShort);
      }
      
      const now = new Date();
      const diffTime = Math.abs(now - regDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    },
    profileCompletionPercentage() {
      if (!this.profile) return 0;
      
      let fieldsToCheck = [
        !!this.profile.name,
        !!this.profile.surname,
        !!this.profile.email,
        !!(this.user && this.user.emailVerified), // Count email verification towards completion
        !!this.profile.profilePicture,
        !!this.profile.university?.name,
        !!this.profile.university?.studentNumber,
        !!this.profile.university?.faculty,
        !!this.profile.university?.courseName,
        !!this.profile.university?.studyYear,
        !!this.profile.university?.levelOfStudy,
        // Check for either the new campuses array or the old campus field
        !!(this.profile.university?.campuses && this.profile.university.campuses.length > 0) || !!this.profile.university?.campus,
        // Add modules to the fields being checked
        !!(this.profile.university?.modules && this.profile.university.modules.length > 0)
      ];
      
      const completedFields = fieldsToCheck.filter(field => field).length;
      return Math.round((completedFields / fieldsToCheck.length) * 100);
    }
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-ZA', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    },
    async logout() {
      await logoutUser();
      this.$router.push('/login');
    },
    async sendVerificationEmail() {
      try {
        await sendVerificationEmail();
        this.verificationEmailSent = true;
        this.$store.dispatch('showAlert', { message: 'Verification email sent! Please check your inbox.', type: 'success' });
      } catch (error) {
        this.$store.dispatch('showAlert', { message: error.message, type: 'error' });
      }
    },
    async savePrivacySettings() {
      try {
        await this.$store.dispatch('updateUserPrivacySettings', this.privacySettings);
        this.$store.dispatch('showAlert', {
          message: 'Privacy settings updated',
          type: 'success'
        });
      } catch (error) {
        this.$store.dispatch('showAlert', {
          message: 'Failed to update privacy settings',
          type: 'error'
        });
      }
    }
  },
  mounted() {
    // Load privacy settings from profile
    if (this.profile?.privacySettings) {
      this.privacySettings = { ...this.privacySettings, ...this.profile.privacySettings };
    }
  },
  watch: {
    'profile.privacySettings': {
      handler(newSettings) {
        if (newSettings) {
          this.privacySettings = { ...this.privacySettings, ...newSettings };
        }
      },
      deep: true
    }
  }
};
</script>

<style scoped>
.header-section {
  background: linear-gradient(135deg, #003366 0%, #1a4d80 100%);
  padding: 2rem 0;
  color: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.header-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 1rem;
}

@media (min-width: 640px) {
  .header-content {
    flex-direction: row;
    text-align: left;
    padding: 0;
  }
}

.profile-image-container {
  position: relative;
  margin-bottom: 1rem;
}

@media (min-width: 640px) {
  .profile-image-container {
    margin-bottom: 0;
    margin-right: 2rem;
  }
}

.profile-image {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid #DAAA00;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.camera-button {
  position: absolute;
  bottom: 0;
  right: 0;
  background-color: white;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  transition: transform 0.2s, background-color 0.2s;
}

.camera-button:hover {
  transform: scale(1.1);
  background-color: #f2f2f2;
}

.camera-button span {
  font-size: 16px;
  color: #003366;
}

.user-details {
  flex: 1;
}

.user-name {
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
}

.user-email {
  font-size: 0.9rem;
  opacity: 0.9;
  margin-bottom: 0.5rem;
}

.member-since {
  font-size: 0.8rem;
  opacity: 0.7;
}

/* Loading state */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 0;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(0, 51, 102, 0.1);
  border-radius: 50%;
  border-top-color: #003366;
  animation: spin 1s ease-in-out infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Cards */
.card {
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  margin-bottom: 1.5rem;
  transition: transform 0.2s, box-shadow 0.2s;
}

.card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #f0f0f0;
}

.card-header h2 {
  font-size: 1.125rem;
  font-weight: 600;
  color: #333;
  margin-left: 0.5rem;
}

.card-header .icon {
  color: #003366;
  font-size: 1.25rem;
}

.edit-btn {
  display: flex;
  align-items: center;
  font-size: 0.875rem;
  color: #003366;
  transition: color 0.2s;
}

.edit-btn:hover {
  color: #DAAA00;
}

.edit-btn span {
  margin-right: 0.25rem;
}

.card-content {
  padding: 1rem 1.5rem;
}

/* Info rows */
.info-row {
  display: flex;
  align-items: center;
  padding: 1rem 0;
  border-bottom: 1px solid #f5f5f5;
}

.info-row:last-child {
  border-bottom: none;
}

.info-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: rgba(0, 51, 102, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
}

.info-icon span {
  color: #003366;
  font-size: 1.25rem;
}

.info-content {
  flex: 1;
}

.info-content h3 {
  font-size: 0.875rem;
  color: #777;
  margin-bottom: 0.25rem;
}

.info-content p {
  font-size: 1rem;
  color: #333;
  font-weight: 500;
}

/* Action buttons */
.action-button {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0.75rem 0;
  background: transparent;
  border: none;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.2s;
  border-bottom: 1px solid #f5f5f5;
}

.action-button:last-child {
  border-bottom: none;
}

.action-button:hover {
  background-color: #f9f9f9;
}

.action-icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: rgba(0, 51, 102, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
}

.action-icon span {
  color: #003366;
  font-size: 1.125rem;
}

.action-content {
  flex: 1;
}

.action-content span {
  font-size: 0.9375rem;
  color: #333;
  font-weight: 500;
}

.arrow {
  color: #ccc;
  transition: transform 0.2s, color 0.2s;
}

.action-button:hover .arrow {
  transform: translateX(2px);
  color: #003366;
}

.action-button.danger .action-content span,
.action-button.danger .action-icon span {
  color: #e31937;
}

.action-icon.danger {
  background-color: rgba(227, 25, 55, 0.1);
}

/* Divider */
.divider {
  height: 1px;
  background-color: #f0f0f0;
  margin: 1rem 0;
}

/* Progress bar */
.progress-container {
  background-color: #f0f0f0;
  border-radius: 10px;
  height: 8px;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #003366 0%, #DAAA00 100%);
  border-radius: 10px;
  transition: width 0.5s ease;
}

/* Add styles for the modules section */
.overflow-y-auto::-webkit-scrollbar {
  width: 8px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 4px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}
</style>
